// src/pages/TicketsTab.js
import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabaseClient'
import { X, ChevronRight } from 'lucide-react'
import { Spinner, TableWrap, Th, Td, Badge, formatRupees, formatTime, timeAgo, SectionTitle } from '../components/ui'

export default function TicketsTab() {
  const [tickets,   setTickets]   = useState([])
  const [loading,   setLoading]   = useState(true)
  const [selected,  setSelected]  = useState(null)
  const [detail,    setDetail]    = useState(null)
  const [note,      setNote]      = useState('')
  const [action,    setAction]    = useState('none')
  const [resolving, setResolving] = useState(false)
  const [reviewing, setReviewing] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.rpc('get_tickets_overview') // 👈 Swapped to RPC
    // const { data } = await supabase
    //   .from('support_tickets')
    //   .select('id, category, status, description, created_at, updated_at, order_id, canteen_id, student_id')
    //   .order('status', { ascending: true }) // OPEN < RESOLVED < UNDER_REVIEW alphabetically
    //   .order('created_at', { ascending: true })
    if (data) setTickets(data)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const openDetail = async (ticket) => {
    setSelected(ticket)
    setNote('')
    setAction('none')
    // Load linked order detail
    if (ticket.order_id) {
      const { data } = await supabase.rpc('get_order_detail', { p_order_id: ticket.order_id })
      setDetail(data)
    } else {
      setDetail(null)
    }
  }

  const markUnderReview = async (ticketId) => {
    setReviewing(ticketId)
    await supabase.rpc('mark_ticket_under_review', { p_ticket_id: ticketId })
    await load()
    setReviewing(null)
    if (selected?.id === ticketId) setSelected(prev => ({ ...prev, status: 'UNDER_REVIEW' }))
  }

  const resolve = async () => {
    if (!note.trim()) return
    setResolving(true)
    const { error } = await supabase.rpc('resolve_ticket', {
      p_ticket_id:       selected.id,
      p_resolution_note: note,
      p_action:          action,
    })
    if (!error) {
      await load()
      setSelected(null)
    }
    setResolving(false)
  }

  // Sort: OPEN first, then UNDER_REVIEW, then RESOLVED
  const sortOrder = { OPEN: 0, UNDER_REVIEW: 1, RESOLVED: 2 }
  const sorted = [...tickets].sort((a, b) => {
    if (sortOrder[a.status] !== sortOrder[b.status]) return sortOrder[a.status] - sortOrder[b.status]
    return new Date(a.created_at) - new Date(b.created_at)
  })

  if (loading) return <Spinner />

  return (
    <div className="flex gap-4 h-full">

      {/* Ticket queue */}
      <div className={`${selected ? 'hidden lg:block lg:w-1/2' : 'w-full'} space-y-4`}>
        <SectionTitle>Support tickets</SectionTitle>
        <TableWrap>
          <thead>
            <tr>
              <Th>ID</Th>
              <Th>Category</Th>
              <Th>Order</Th>
              <Th>Status</Th>
              <Th>Opened</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(t => (
              <tr key={t.id}
                onClick={() => openDetail(t)}
                className={`cursor-pointer hover:bg-white/[0.02] transition-colors ${selected?.id === t.id ? 'bg-orange-500/5' : ''}`}>
                <Td mono><span className="text-gray-300">#{t.id}</span></Td>
                <Td>
                  <span className="text-gray-300 capitalize">{t.category.replace('_', ' ')}</span>
                </Td>
                <Td mono>
                  {t.order_id
                    ? <span className="text-gray-500">{t.order_id.slice(0,8)}…</span>
                    : <span className="text-gray-700">—</span>}
                </Td>
                <Td><Badge label={t.status} /></Td>
                <Td><span className="text-gray-500">{timeAgo(t.created_at)}</span></Td>
                <Td>
                  {t.status === 'OPEN' && (
                    <button
                      onClick={e => { e.stopPropagation(); markUnderReview(t.id) }}
                      disabled={reviewing === t.id}
                      className="text-[10px] font-bold uppercase tracking-widest text-amber-400 hover:text-amber-300 disabled:opacity-50 whitespace-nowrap">
                      {reviewing === t.id ? '...' : 'Review →'}
                    </button>
                  )}
                </Td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr><td colSpan={6} className="text-center text-gray-600 py-8 text-xs">No tickets</td></tr>
            )}
          </tbody>
        </TableWrap>
      </div>

      {/* Detail panel */}
      {selected && (
        <div className="w-full lg:w-1/2 bg-[#1c1c1e] rounded-2xl border border-white/5 p-5 overflow-y-auto space-y-5 flex-shrink-0">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white font-semibold">Ticket #{selected.id}</p>
              <p className="text-blue-400 text-xs mt-0.5">{selected.student_email}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge label={selected.status} />
                <span className="text-gray-600 text-[10px]">{selected.category?.replace('_',' ')}</span>
              </div>
            </div>
            <button onClick={() => setSelected(null)} className="text-gray-600 hover:text-gray-400">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Student description */}
          <div>
            <p className="text-gray-500 text-[10px] uppercase tracking-widest font-bold mb-2">Student description</p>
            <p className="text-gray-300 text-sm leading-relaxed bg-[#121212] rounded-xl p-3 border border-white/5">
              {selected.description}
            </p>
            <p className="text-gray-600 text-[10px] mt-1">Opened {formatTime(selected.created_at)}</p>
          </div>

          {/* Linked order */}
          {detail && (
            <div>
              <p className="text-gray-500 text-[10px] uppercase tracking-widest font-bold mb-2">Linked order</p>
              <div className="bg-[#121212] rounded-xl p-3 border border-white/5 space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Order ID</span>
                  <span className="text-gray-300 font-mono">{selected.order_id?.slice(0,16)}…</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Status</span>
                  <Badge label={detail.order?.status} />
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Amount</span>
                  <span className="text-orange-400">{formatRupees(detail.order?.total_amount)}</span>
                </div>
                {detail.payment && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">TXN ID</span>
                    <span className="text-gray-300 font-mono text-[10px]">{detail.payment.txn_id?.slice(0,20)}…</span>
                  </div>
                )}
                {detail.refund && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Refund</span>
                    <Badge label={detail.refund.status} />
                  </div>
                )}
                {/* Items */}
                {detail.items?.length > 0 && (
                  <div className="pt-2 border-t border-white/5 space-y-1">
                    {detail.items.map((i, idx) => (
                      <div key={idx} className="flex justify-between text-gray-400">
                        <span>{i.quantity}× {i.name}</span>
                        <span>{formatRupees(i.price * i.quantity)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Resolve section */}
          {selected.status !== 'RESOLVED' && (
            <div className="space-y-3 pt-2 border-t border-white/10">
              <p className="text-gray-500 text-[10px] uppercase tracking-widest font-bold">Resolve ticket</p>
              <textarea
                value={note} onChange={e => setNote(e.target.value)}
                placeholder="Resolution note (required)..."
                rows={3}
                className="w-full bg-[#121212] border border-white/10 rounded-xl px-3 py-2.5 text-gray-300 text-xs focus:border-orange-500 focus:outline-none resize-none placeholder-gray-700"
              />
              <select value={action} onChange={e => setAction(e.target.value)}
                className="w-full bg-[#121212] border border-white/10 rounded-xl px-3 py-2.5 text-gray-300 text-xs focus:border-orange-500 focus:outline-none">
                <option value="none">No action</option>
                <option value="trigger_refund">Trigger refund</option>
                <option value="mark_webhook_resolved">Mark webhook resolved</option>
              </select>
              <button onClick={resolve} disabled={resolving || !note.trim()}
                className="w-full bg-orange-500 hover:bg-orange-400 text-white py-2.5 rounded-xl font-semibold text-sm tracking-wide disabled:opacity-50 transition-all">
                {resolving ? 'Resolving...' : 'Resolve ticket'}
              </button>
            </div>
          )}

          {selected.status === 'RESOLVED' && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3">
              <p className="text-green-400 text-xs font-bold uppercase tracking-widest mb-1">Resolved</p>
              <p className="text-gray-300 text-sm leading-relaxed">{selected.resolution_note || '—'}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}